import React, { useEffect } from "react";
import { connect } from "react-redux";
import { useBoolean, useNumber } from "react-hanger";
import styled from "styled-components";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Slide from "@mui/material/Slide";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import StepContent from "@mui/material/StepContent";
import Stepper from "@mui/material/Stepper";
import Typography from "@mui/material/Typography";
import ErrorIcon from "@mui/icons-material/Error";
import ReactPlayer from "react-player";
import LiveChat from "react-livechat";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import TextLoop from "react-text-loop";

import { LIVECHAT_LICENSE, STRIPE_PUBLISHABLE_KEY } from "../../../constants";
import { Emphasize } from "../../styles";
import { Step1Content, Step2Content, Step3Content, Step4Content } from "./steps";
import BundleCard from "../../bundleCard";
import hbmLogo from "../../../assets/images/hbm.png";
import AcmaIcon from "../../../assets/images/icon_acma.webp";
import AhkIcon from "../../../assets/images/icon_ahk.webp";
import ApacdIcon from "../../../assets/images/icon_apacd.webp";
import AustChamIcon from "../../../assets/images/icon_austcham.webp";
import FacebookIcon from "../../../assets/images/icon_facebook.webp";
import InstagramIcon from "../../../assets/images/icon_instagram.webp";
import IrpasIcon from "../../../assets/images/icon_irpas.webp";
import LinkedInIcon from "../../../assets/images/icon_linkedin.webp";
import MyNewsDeskIcon from "../../../assets/images/icon_mynewsdesk.webp";
import SgTechIcon from "../../../assets/images/icon_sgtech.webp";
import SidIcon from "../../../assets/images/icon_sid.webp";
import TwitterIcon from "../../../assets/images/icon_twitter.webp";
import YoutubeIcon from "../../../assets/images/icon_youtube.webp";
import MockupBook from "../../../assets/images/MockUp_Book2-02.jpg"
import { csActions, visitorActions } from "../../../redux/actions";

const Transition = React.forwardRef((props, ref) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);

const MemberOfIcon = styled.img`
  margin-right: 20px;
  margin-bottom: 10px;
`;

const HomePage = (props) => {
  const activeStep = useNumber(0);
  const isAvailableBundlesDialogOpen = useBoolean(false);

  const handleShowAvailableBundles = () => {
    isAvailableBundlesDialogOpen.setTrue();
    props.getBundles();
  }

  const handleAvailableBundlesDialogClose = () => {
    isAvailableBundlesDialogOpen.setFalse();
  }

  const handleNextStep = () => {
    activeStep.increase();
  }

  const handleBackStep = () => {
    activeStep.decrease();
  }

  const steps = [
    {
      label: "Upload CliftonStrength 34 Report",
      content: <Step1Content handleNextStep={handleNextStep} handleBackStep={handleBackStep} />,
    },
    {
      label: "Enter email address",
      content: <Step2Content handleNextStep={handleNextStep} handleBackStep={handleBackStep} />,
    },
    {
      label: "Choose bundle",
      content: (
        <Elements stripe={stripePromise}>
          <Step3Content handleNextStep={handleNextStep} handleBackStep={handleBackStep} />
        </Elements>
      ),
    },
    {
      label: "Book workshop/access online course",
      content: <Step4Content handleNextStep={handleNextStep} handleBackStep={handleBackStep} />,
    },
  ];

  useEffect(() => {
    props.getOrCreateVisitorId();
  }, []);

  return (
    <>
      <AppBar
        position="sticky"
        sx={{
          height: "80px",
          backgroundColor: "#ffffff",
          color: "rgb(220, 1, 0)",
          boxShadow: "rgba(220, 1, 0, 0.2) 0px 2px 4px -1px, rgba(220, 1, 0, 0.14) 0px 4px 5px 0px, rgba(220, 1, 0, 0.12) 0px 1px 10px 0px",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            padding: "10px 0",
          }}
        >
          <img src={hbmLogo} height="100%" style={{marginRight: "40px"}}/>
          <TextLoop>
            <span>
              <Typography
                sx={{
                  letterSpacing: "4px",
                  fontWeight: 500,
                }}
              >
                MEDIA & PRESENTATION TRAINING
              </Typography>
            </span>
            <span>
              <Typography
                sx={{
                  letterSpacing: "4px",
                  fontWeight: 500,
                }}
              >
                POWERED BY CLIFTONSTRENGTHS
              </Typography>
            </span>
          </TextLoop>
        </Container>
      </AppBar>
      <Container
        maxWidth="lg"
        sx={{
          marginTop: "30px",
          textAlign: "center",
        }}
      >
        <Box sx={{ width: "100%", padding: "10px 0" }}>
          <Typography variant="h4">
            Media & presentation training, powered by CliftonStrengths
          </Typography>
        </Box>
        <Box sx={{ width: "100%", padding: "10px 0", marginBottom: "16px" }}>
          <Typography variant="h6">
            We tailor every workshop to suit the unique strengths of each individual participant.
          </Typography>
          <Typography variant="subtitle1">
            Whether you're a naturally strong presenter or not, our <Emphasize>Gallup-certified CliftonStrengths coach Mark Laudi</Emphasize> brings out the best presenter in you.
          </Typography>
        </Box>
        <ReactPlayer
          url="https://vimeo.com/showcase/11109702"
          controls={true}
          style={{
            margin: "auto",
          }}
        />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginTop: "40px",
            gap: "20px",
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography variant="h6">
                Get your copy of <Emphasize>"Play to your Strengths when Presenting"</Emphasize> book.
              </Typography>
            </Box>
            <Box sx={{ width: "100%", marginTop: "30px" }}>
              <Typography variant="subtitle1">
                You are just <Emphasize>FOUR STEPS</Emphasize> away from being a more confident, eloquent and comfortable presenter,
                in virtual meetings and presentations, and in the media.
              </Typography>
              <Typography variant="h6" sx={{marginTop: "45px"}}>
                Check out available bundles&nbsp;
                <Link
                  underline="hover"
                  onClick={handleShowAvailableBundles}
                  sx={{cursor: "pointer"}}
                >
                  here
                </Link>.
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              flex: 0.95,
            }}
          >
            <img src={MockupBook} alt="sample report" width="100%"/>
          </Box>
        </Box>
        <Container
          maxWidth="lg"
          sx={{
            padding: "20px 0",
          }}
        >
          <Stepper activeStep={activeStep.value} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={index}>
                <StepLabel>{step.label}</StepLabel>
                <StepContent>
                  <Box sx={{ mb: 2 }}>
                    {step.content}
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </Container>
        <Box
          sx={{
            width: "100%",
            marginTop: "20px",
          }}
        >
          <Typography variant="caption">
            Disclaimer:<br />
            CliftonStrengths, CliftonStrengths Theme Names and Theme descriptions are trademarks of Gallup, Inc.<br />
            This portal and any documents we provide are purely for your personal use as part of our coaching and training programs.<br />
            They are not to be shared, reproduced, or in any other way made publicly available.<br />
            For full details visit&nbsp;
            <Link
              href="https://my.gallup.com/_Home/ProductTerms"
              target="_blank"
              rel="noopener"
              underline="hover"
            >
              Gallup | Analytics and Tools to Transform Your Workspace - Product Terms of Use
            </Link>.
          </Typography>
        </Box>
      </Container>
      <Box
        sx={{
          width: "100%",
          marginTop: "64px",
          backgroundColor: "rgb(61, 61, 61)",
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            padding: "30px 0",
            color: "#ffffff",
            display: "flex",
            gap: "64px",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box sx={{ marginBottom: "20px" }}>
              <Typography variant="h6">ABOUT US</Typography>
            </Box>
            <Typography variant="body1">
              Hong Bao Media provides a new generation of communication services to corporate clients throughout the Asia Pacific, for a new era of communications.
            </Typography>
          </Box>
          <Box sx={{ width: "240px" }}>
            <Box sx={{ marginBottom: "20px" }}>
              <Typography variant="h6">OUR OFFICES</Typography>
            </Box>
            <Box sx={{ marginBottom: "20px" }}>
              <Typography variant="body1">SINGAPORE</Typography>
              <Typography variant="body1">138 Robinson Road</Typography>
              <Typography variant="body1">#21-02 Oxley Tower Lobby 1</Typography>
              <Typography variant="body1">Singapore 068906</Typography>
            </Box>
            <Typography variant="body1">MALAYSIA</Typography>
            <Typography variant="body1">800 Jalan Sentul</Typography>
            <Typography variant="body1">D6 @ Sentul East</Typography>
            <Typography variant="body1">Unit 1-9</Typography>
            <Typography variant="body1">51000 Kuala Lumpur</Typography>
            <Typography variant="body1">Malaysia</Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ marginBottom: "20px" }}>
              <Typography variant="h6">FOLLOW US</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: "24px" }}>
              <Link
                href="http://www.mynewsdesk.com/sg/hong-bao-media"
                target="_blank"
                rel="noopener"
              >
                <img src={MyNewsDeskIcon} alt="MyNewsDesk" />
              </Link>
              <Link
                href="https://www.linkedin.com/company/hongbaomedia"
                target="_blank"
                rel="noopener"
              >
                <img src={LinkedInIcon} alt="LinkedIn" />
              </Link>
              <Link
                href="https://twitter.com/HongBaoMedia"
                target="_blank"
                rel="noopener"
              >
                <img src={TwitterIcon} alt="Twitter" />
              </Link>
              <Link
                href="https://www.facebook.com/hongbaomedia/"
                target="_blank"
                rel="noopener"
              >
                <img src={FacebookIcon} alt="Facebook" />
              </Link>
              <Link
                href="https://www.youtube.com/user/hongbaomedia"
                target="_blank"
                rel="noopener"
              >
                <img src={YoutubeIcon} alt="Youtube" />
              </Link>
              <Link
                href="https://instagram.com/hongbaomedia"
                target="_blank"
                rel="noopener"
              >
                <img src={InstagramIcon} alt="Instagram" />
              </Link>
            </Box>
            <Box sx={{ marginTop: "30px", marginBottom: "20px" }}>
              <Typography variant="h6">MEMBER OF</Typography>
            </Box>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}>
              <MemberOfIcon src={ApacdIcon} />
              <MemberOfIcon src={AhkIcon} />
              <MemberOfIcon src={AustChamIcon} />
              <MemberOfIcon src={SgTechIcon} />
              <MemberOfIcon src={SidIcon} />
              <MemberOfIcon src={IrpasIcon} />
              <MemberOfIcon src={AcmaIcon} />
            </Box>
          </Box>
        </Container>
      </Box>
      <Dialog
        open={isAvailableBundlesDialogOpen.value}
        TransitionComponent={Transition}
        keepMounted
        maxWidth="xl"
        onClose={handleAvailableBundlesDialogClose}
      >
        <DialogTitle>Available Bundles</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", margin: "8px 0" }}>
            {props.isError &&
              <Grid container direction="row" alignItems="center" justifyContent="center" spacing="2px">
                <Grid item>
                  <ErrorIcon color="error" fontSize="small" />
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="error">{props.errorMessage}</Typography>
                </Grid>
              </Grid>
            }
            {props.isGettingBundles &&
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CircularProgress color="primary" />
              </Box>
            }
            {!props.isGettingBundles && props.bundles &&
              <Container
                sx={{
                  padding: "0 !important",
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  gap: "20px",
                  width: "100%",
                }}
              >
                {props.bundles.map((bundle, index) => (
                  <BundleCard key={index} bundle={bundle} />
                ))}
              </Container>
            }
          </Box>
        </DialogContent>
      </Dialog>
      <LiveChat license={LIVECHAT_LICENSE} />
    </>
  );
}

const mapState = (state) => {
  const {
    isGettingBundles,
    bundles,
    isError,
    errorMessage,
  } = state.cs;
  return {
    isGettingBundles,
    bundles,
    isError,
    errorMessage,
  };
};

const actionCreators = {
  getOrCreateVisitorId: visitorActions.getOrCreateId,
  getBundles: csActions.getBundles,
};

export default connect(mapState, actionCreators)(HomePage);